import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import Alert from '@material-ui/lab/Alert';
import useApi from '../../../utils/useApi';

const useStyles = makeStyles(() => ({
  regenerateBtn: {
    color: '#17497F',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: 6,
    padding: '2px 8px',
    fontSize: '0.75rem',
    minWidth: 'auto',
    whiteSpace: 'nowrap',
    marginRight: -6,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(23, 73, 127, 0.08)',
    },
    '&:disabled': {
      color: '#94a3b8',
    },
  },
  dialogTitle: {
    fontWeight: 700,
    color: '#17497F',
  },
}));

export default function UIBarcodeRegenerateButton({
  itemId = null,
  itemType = 'product',
  currentBarCode = '',
  onGenerated,
}) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const endpoint = itemType === 'service' ? '/services/barcode' : '/products/barcode';

  const [regenerateApi] = useApi({
    method: 'post',
    url: endpoint,
  });

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const hasCurrent = Boolean(currentBarCode && String(currentBarCode).trim());

  const executeRegenerate = () => {
    setOpenConfirmDialog(false);
    setLoading(true);
    const payload = itemId ? { id: Number(itemId) } : {};

    regenerateApi({
      data: payload,
    })
      .then((res) => {
        setLoading(false);
        const newCode = res?.data?.barCode;
        if (newCode) {
          if (onGenerated) {
            onGenerated(newCode);
          }
          const successMsg = itemId
            ? `Novo código (${newCode}) gerado e salvo com sucesso!`
            : `Novo código (${newCode}) gerado com sucesso!`;
          showToast(successMsg, 'success');
        } else if (res?.data?.error) {
          showToast(res.data.error, 'error');
        } else {
          showToast('Não foi possível regerar o código de barras.', 'error');
        }
      })
      .catch((err) => {
        setLoading(false);
        const errMsg = err?.response?.data?.error || 'Erro ao regerar o código de barras.';
        showToast(errMsg, 'error');
      });
  };

  const handleClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (loading) return;

    if (hasCurrent || itemId) {
      setOpenConfirmDialog(true);
    } else {
      executeRegenerate();
    }
  };

  const buttonLabel = loading ? 'Gerando...' : (hasCurrent ? 'Regerar' : 'Gerar');
  const tooltipText = itemId
    ? 'Gerar novo código padrão EAN-13 e salvar automaticamente'
    : 'Gerar novo código padrão EAN-13 automaticamente';

  return (
    <>
      <Tooltip title={tooltipText}>
        <span>
          <Button
            type="button"
            size="small"
            className={classes.regenerateBtn}
            onClick={handleClick}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <AutorenewIcon style={{ fontSize: 16 }} />
              )
            }
          >
            {buttonLabel}
          </Button>
        </span>
      </Tooltip>

      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        aria-labelledby="barcode-confirm-title"
        aria-describedby="barcode-confirm-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="barcode-confirm-title" className={classes.dialogTitle}>
          Regerar código de barras?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="barcode-confirm-description">
            {hasCurrent && itemId ? (
              <>
                Tem certeza que deseja regerar o código de barras? O código atual (
                <strong>{currentBarCode}</strong>) será{' '}
                <strong>sobrescrito e salvo imediatamente</strong>.
                <br />
                <br />
                Atenção: etiquetas impressas anteriormente com o código antigo deixarão de identificar este item.
              </>
            ) : hasCurrent ? (
              <>
                O código preenchido atualmente (<strong>{currentBarCode}</strong>) será substituído por um novo código padrão EAN-13. Deseja continuar?
              </>
            ) : (
              <>
                Um novo código padrão EAN-13 será gerado e <strong>salvo imediatamente</strong> para este item no banco de dados. Deseja continuar?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px', gap: 8 }}>
          <Button onClick={() => setOpenConfirmDialog(false)} color="default">
            Cancelar
          </Button>
          <Button
            onClick={executeRegenerate}
            variant="contained"
            color="primary"
            disableElevation
            style={{ backgroundColor: '#17497F', fontWeight: 600 }}
          >
            {itemId ? 'Sim, regerar e salvar' : 'Sim, regerar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
